
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Copy, Globe, QrCode } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import QRCodeGenerator from "react-qr-code";

interface PublishDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPublish: (slug: string) => void;
  currentSlug?: string;
  isLoading: boolean;
}

export function PublishDialog({ 
  open, 
  onOpenChange, 
  onPublish, 
  currentSlug, 
  isLoading 
}: PublishDialogProps) {
  const [slug, setSlug] = useState(currentSlug || '');
  const [showQR, setShowQR] = useState(false);

  const generateSlug = () => {
    const randomSlug = `landing-${Math.random().toString(36).substr(2, 8)}`;
    setSlug(randomSlug);
  };

  const handlePublish = () => {
    if (!slug.trim()) {
      toast({
        title: "Error",
        description: "Please enter a slug for your landing page",
        variant: "destructive",
      });
      return;
    }
    onPublish(slug.trim());
  };

  const copyUrl = () => {
    const url = `${window.location.origin}/landing/${slug}`;
    navigator.clipboard.writeText(url);
    toast({
      title: "Copied",
      description: "Landing page URL copied to clipboard",
    });
  };

  const landingUrl = `${window.location.origin}/landing/${slug}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[90vw] max-w-[600px] mx-auto p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <DialogHeader className="space-y-3">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Globe className="h-5 w-5" />
            Publish Landing Page
          </DialogTitle>
          <DialogDescription className="text-sm">
            Make your landing page accessible to the public with a custom URL.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-3">
            <Label htmlFor="slug" className="text-sm font-medium">Landing Page URL</Label>
            <div className="space-y-3">
              <div className="flex flex-col gap-2">
                <div className="w-full">
                  <div className="flex flex-col sm:flex-row">
                    <span className="inline-flex items-center px-3 py-2 text-xs text-gray-500 bg-gray-50 border border-r-0 sm:border-r border-gray-300 rounded-t-md sm:rounded-l-md sm:rounded-t-none whitespace-nowrap">
                      {window.location.origin}/landing/
                    </span>
                    <Input
                      id="slug"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                      placeholder="my-restaurant"
                      className="rounded-b-md sm:rounded-l-none sm:rounded-b-none sm:rounded-r-md text-sm min-w-0"
                    />
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={generateSlug}
                  disabled={isLoading}
                  className="w-full sm:w-auto text-sm"
                >
                  Generate Random Slug
                </Button>
              </div>
              {slug && (
                <div className="p-3 bg-gray-50 rounded-md">
                  <p className="text-xs text-muted-foreground break-all leading-relaxed">
                    Your landing page will be available at: 
                    <br className="sm:hidden" />
                    <span className="font-mono text-blue-600">{landingUrl}</span>
                  </p>
                </div>
              )}
            </div>
          </div>

          {currentSlug && (
            <div className="space-y-4 p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <Badge variant="default" className="bg-green-100 text-green-800 text-xs">
                  Currently Published
                </Badge>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyUrl}
                    className="text-xs flex items-center gap-1"
                  >
                    <Copy className="h-3 w-3" />
                    Copy URL
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowQR(!showQR)}
                    className="text-xs flex items-center gap-1"
                  >
                    <QrCode className="h-3 w-3" />
                    {showQR ? 'Hide QR' : 'Show QR'}
                  </Button>
                </div>
              </div>

              {showQR && (
                <div className="flex justify-center p-4 bg-white rounded-lg border">
                  <QRCodeGenerator value={landingUrl} size={150} />
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-3 sm:gap-2 pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            className="w-full sm:w-auto order-2 sm:order-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handlePublish}
            disabled={!slug.trim() || isLoading}
            className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 order-1 sm:order-2"
          >
            {isLoading ? "Publishing..." : currentSlug ? "Update" : "Publish"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
